/**
 * The DCF resubmittals board's model (docs/design/kanban.md): pure data and functions shared by the board, its docked
 * panel and the tests.
 *
 * A card is ONE DCF RESUBMITTAL. The server places it (`state` is its column); nothing here derives a column. A drop
 * never writes: RESUBMITTAL_TRANSITIONS says which drops OPEN a governed act's form in the docked panel, and every other
 * drop is refused in one line. UMED, not Finance, records the sending and DCF's answer, so no drop claims them.
 *
 * The UI word is "DCF resubmittal". "Full reversal" is only the label of the FULL kind.
 */
import type { TransitionMap, KanbanCardBase, KanbanChip } from '../../components/board/kanban';
import type { ResubmittalCardRow, CorrectedTerms, ResubmittalKind } from './resubmittalsApi';

export type ResubmittalAct = 'hand-to-umed' | 'withdraw' | 'correction' | 'reason';

export interface ResubmittalCard extends KanbanCardBase, ResubmittalCardRow {
  id: string;
  column: string;
}

export interface ResubmittalCtx { canManage: boolean }

export const cardFromRow = (r: ResubmittalCardRow): ResubmittalCard => ({ ...r, column: r.state });

export const READ_ONLY_WHY = 'Read-only: your role does not hold the DCF resubmittal acts.';
export const SENDING_REFUSAL = 'UMED records the sending when the packet is faxed.';
export const ANSWER_REFUSAL = "UMED records DCF's answer on the UMED case.";
export const BACKWARD_REFUSAL = 'Resubmittals do not move backward: each step is recorded once.';

export const KIND_LABEL: Record<ResubmittalKind, string> = { CORRECTED: 'Corrected', FULL: 'Full reversal' };
export const KIND_NOT_STATED = 'Kind not stated';
export const REQUESTER_LABEL: Record<string, string> = {
  FACILITY: 'Facility', RESPONSIBLE_PARTY: 'Responsible party', DCF: 'DCF', SFS: 'SFS', OTHER: 'Other',
};

/** Whole cents as dollars with two decimals. Null is "not recorded", never $0.00. */
export function dollars(cents: number | null | undefined): string | null {
  if (cents === null || cents === undefined || !Number.isFinite(Number(cents))) return null;
  return (Number(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * What a person typed as dollars ("1,200", "$1,200.50") as integer cents, or null when it is not a plain money amount.
 * Parsed from the digits, never through floating point, so 19.99 is 1999.
 */
export function dollarsToCents(input: string): number | null {
  const s = String(input).trim().replace(/^\$/, '').replace(/,/g, '');
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const cents = Number(m[1]) * 100 + Number((m[2] || '').padEnd(2, '0') || '0');
  return Number.isSafeInteger(cents) ? cents : null;
}

/** The typed correction. Strings, as the inputs hold them. */
export interface TermsFields { months: string; monthly: string; total: string; startsOn: string; endsOn: string; text: string }
export const EMPTY_TERMS: TermsFields = { months: '', monthly: '', total: '', startsOn: '', endsOn: '', text: '' };

/** The typed fields as the request's `corrected_terms` (integer cents), or the one thing wrong. */
export function termsBody(f: TermsFields): { ok: true; terms: CorrectedTerms } | { ok: false; message: string } {
  const months = /^\d+$/.test(f.months.trim()) ? Number(f.months.trim()) : NaN;
  if (!Number.isInteger(months) || months < 1 || months > 60) return { ok: false, message: 'The term is a whole number of months, from 1 to 60.' };
  const monthly = dollarsToCents(f.monthly);
  if (monthly === null || monthly <= 0) return { ok: false, message: 'The monthly amount is dollars and cents, more than nothing.' };
  const total = dollarsToCents(f.total);
  if (total === null || total <= 0) return { ok: false, message: 'The corrected total is dollars and cents, more than nothing. A total of nothing is a full reversal: choose that kind instead.' };
  if (f.startsOn && f.endsOn && f.endsOn < f.startsOn) return { ok: false, message: 'The term cannot end before it starts.' };
  const terms: CorrectedTerms = { months, monthly_cents: monthly, total_cents: total };
  if (f.startsOn) terms.starts_on = f.startsOn;
  if (f.endsOn) terms.ends_on = f.endsOn;
  if (f.text.trim()) terms.terms_text = f.text.trim();
  return { ok: true, terms };
}

/** The correction as one line: "12 months at $100.00 a month · $1,200.00" (the typed words win when the server kept them). */
export function termsLine(t: CorrectedTerms | null | undefined): string | null {
  if (!t) return null;
  if (t.terms_text) return t.terms_text;
  const monthly = dollars(t.monthly_cents);
  const total = dollars(t.total_cents);
  return `${t.months} ${t.months === 1 ? 'month' : 'months'}${monthly ? ` at ${monthly} a month` : ''}${total ? ` · ${total}` : ''}`;
}

/** The reason, shortened for a card. */
export function truncate(s: string | null | undefined, max = 80): string {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** The kind chip: Corrected / Full reversal, or the warning that a legacy card never stated one. */
export function kindChip(card: ResubmittalCardRow): KanbanChip {
  if (card.kind === 'CORRECTED') return { key: 'kind', label: KIND_LABEL.CORRECTED, tone: 'info' };
  if (card.kind === 'FULL') return { key: 'kind', label: KIND_LABEL.FULL, tone: 'neutral' };
  return { key: 'kind', label: KIND_NOT_STATED, tone: 'warn' };
}

const UMED_COLUMNS: string[] = ['WITH_UMED', 'WITH_DCF', 'DCF_DECLINED'];

/** At most two chips: the kind, then (UMED's columns) the packet's first blocker, or the sending count on a decline. */
export function chipsFor(card: ResubmittalCardRow): KanbanChip[] {
  const out: KanbanChip[] = [kindChip(card)];
  if (card.state === 'DCF_DECLINED') {
    const n = card.submission_count;
    out.push({ key: 'sendings', label: `Sent ${n} ${n === 1 ? 'time' : 'times'}`, tone: 'warn' });
  } else if (UMED_COLUMNS.includes(card.state) && card.packet) {
    const first = Array.isArray(card.packet.blockers) ? card.packet.blockers[0] : null;
    if (first) out.push({ key: 'blocker', label: first, tone: 'stop' });
    else if (card.packet.state) out.push({ key: 'packet', label: `Packet ${String(card.packet.state).toLowerCase().replace(/_/g, ' ')}`, tone: 'neutral' });
  }
  return out;
}

/** Why a hand to UMED is not on offer right now, or true. */
export function handAvailable(card: ResubmittalCardRow, ctx: ResubmittalCtx): true | string {
  if (!ctx.canManage) return READ_ONLY_WHY;
  if (!card.kind) return 'State whether this is a corrected or a full resubmittal before handing it to UMED.';
  if (card.kind === 'CORRECTED' && !card.corrected_terms) return 'State the corrected terms before handing it to UMED.';
  return true;
}

/** Why a withdrawal is not on offer right now, or true. */
export function withdrawAvailable(card: ResubmittalCardRow, ctx: ResubmittalCtx): true | string {
  if (!ctx.canManage) return READ_ONLY_WHY;
  if (!Array.isArray(card.withdrawable_by) || !card.withdrawable_by.includes('FINANCE')) return 'Finance can no longer withdraw this: it has been sent.';
  return true;
}

const refuse = (reason: string) => ({ kind: 'refuse' as const, reason });

export const RESUBMITTAL_TRANSITIONS: TransitionMap<ResubmittalCard, ResubmittalCtx> = {
  moves: {
    WITH_FINANCE: {
      WITH_UMED: { kind: 'act', act: 'hand-to-umed', label: 'Hand to UMED…', opens: 'Opens Hand to UMED, which shows what UMED will receive. Nothing is recorded until you confirm.', available: handAvailable },
      WITHDRAWN: { kind: 'act', act: 'withdraw', label: 'Withdraw…', opens: 'Opens Withdraw, which asks for the reason. Nothing is recorded until you confirm.', available: withdrawAvailable },
      WITH_DCF: refuse(SENDING_REFUSAL),
      DCF_DECLINED: refuse(ANSWER_REFUSAL),
      APPROVED: refuse(ANSWER_REFUSAL),
    },
    WITH_UMED: {
      WITHDRAWN: { kind: 'act', act: 'withdraw', label: 'Withdraw…', opens: 'Opens Withdraw, which asks for the reason. Nothing is recorded until you confirm.', available: withdrawAvailable },
      WITH_DCF: refuse(SENDING_REFUSAL),
      DCF_DECLINED: refuse(ANSWER_REFUSAL),
      APPROVED: refuse(ANSWER_REFUSAL),
    },
    WITH_DCF: {
      APPROVED: refuse(ANSWER_REFUSAL),
      DCF_DECLINED: refuse(ANSWER_REFUSAL),
    },
    DCF_DECLINED: {
      WITH_DCF: refuse(SENDING_REFUSAL),
      APPROVED: refuse(ANSWER_REFUSAL),
    },
  },
  fallbackReason: () => BACKWARD_REFUSAL,
  // Acts a card takes where it stands. Listed in Move… only when on offer.
  inPlace: (card, ctx) => {
    if (!ctx.canManage) return [];
    const out: Array<{ act: string; label: string; opens?: string; available: true }> = [];
    if (card.state === 'WITH_FINANCE') {
      out.push({ act: 'correction', label: 'State the correction…', opens: 'Opens the kind and the corrected terms. Nothing is recorded until you save.', available: true });
      out.push({ act: 'reason', label: 'Add a reason…', opens: 'Opens a field for one more reason. Nothing is recorded until you save.', available: true });
    } else if (card.state === 'WITH_UMED' && card.kind === null) {
      out.push({ act: 'correction', label: 'State the kind…', opens: 'This card never stated its kind. Opens the kind and the corrected terms; it can be stated once.', available: true });
    }
    return out;
  },
  canDrag: (card, ctx) => ctx.canManage && card.state !== 'APPROVED' && card.state !== 'WITHDRAWN',
};

/** Which acts the docked panel offers a card (the same rule as Move…'s, without a drag). */
export function actsOn(card: ResubmittalCardRow, ctx: ResubmittalCtx): ResubmittalAct[] {
  if (!ctx.canManage) return [];
  const out: ResubmittalAct[] = [];
  if (card.state === 'WITH_FINANCE') out.push('correction', 'reason', 'hand-to-umed');
  else if (card.state === 'WITH_UMED' && card.kind === null) out.push('correction');
  if (withdrawAvailable(card, ctx) === true) out.push('withdraw');
  return out;
}
